// API helpers for the Test Audio Files feature.
//
// Dev-only / Ethosphere-org-only — the backend gates every endpoint by env
// + org and returns 404 (not 403) when the caller is outside the gate.
// The single exception is `getTestAudioFeatureStatus` which always returns
// `{enabled: bool}` so the FE can decide whether to render the tab.
//
// Endpoints (backend `app/endpoints.py`):
//   GET    /test-audio-files/feature-status
//   GET    /test-audio-files                        (org-scoped list)
//   POST   /test-audio-files                        (register a fresh upload)
//   GET    /test-audio-files/{id}
//   GET    /test-audio-files/{id}/audio-url         (presigned S3 GET)
//   DELETE /test-audio-files/{id}                   (soft delete)

import { Api } from "../Api";

/**
 * GET /test-audio-files/feature-status
 *
 * Always 200. Returns `{enabled: true}` only when both the backend env flag
 * (`TEST_AUDIO_FILES_ENABLED=true`) and the caller's `organization_id ==
 * ETHOSPHERE_ORG_ID` are satisfied. Frontend calls this on mount of the
 * top-level chrome and hides the tab when `enabled === false`.
 */
export async function getTestAudioFeatureStatus() {
  try {
    const response = await Api.get("/test-audio-files/feature-status");
    return response.data;
  } catch (e) {
    // 401/403 from unauthenticated callers — treat as "not available".
    // Anything 5xx — also "not available" so we never *block* the rest of
    // the app rendering due to a flag-fetch failure.
    return { enabled: false };
  }
}

/**
 * GET /test-audio-files?status=READY&limit=50
 */
export async function listTestAudioFiles({ status = "READY", limit = 50 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (status) params.set("status", status);
  const response = await Api.get(`/test-audio-files?${params.toString()}`);
  return response.data;
}

/**
 * POST /test-audio-files — register a freshly-completed multipart upload.
 *
 * The caller MUST first complete an `audioUploader.uploadBlobMultipart`
 * with `uploadType="test_audio_source"` and pass the resulting
 * `bucketName` + `filePath` here.
 */
export async function createTestAudioFile({
  name,
  description,
  sourceS3Bucket,
  sourceS3Key,
  sourceFilename,
  sourceMimeType,
}) {
  const response = await Api.post("/test-audio-files", {
    name,
    description,
    source_s3_bucket: sourceS3Bucket,
    source_s3_key: sourceS3Key,
    source_filename: sourceFilename,
    source_mime_type: sourceMimeType,
  });
  return response.data;
}

/**
 * GET /test-audio-files/{id}
 */
export async function getTestAudioFile(fileId) {
  const response = await Api.get(`/test-audio-files/${fileId}`);
  return response.data;
}

/**
 * GET /test-audio-files/{id}/audio-url
 *
 * Returns `{url, expires_in}` for the normalised WAV. Only works when
 * `status === "READY"`. The URL is good for ~15 minutes — the Run dialog
 * loads it into WaveSurfer immediately.
 */
export async function getTestAudioFileAudioUrl(fileId) {
  const response = await Api.get(`/test-audio-files/${fileId}/audio-url`);
  return response.data;
}

/**
 * DELETE /test-audio-files/{id} — soft delete. Backend 403s if the caller
 * is neither the uploader nor an admin.
 */
export async function deleteTestAudioFile(fileId) {
  await Api.delete(`/test-audio-files/${fileId}`);
}
